import { useTranslation } from 'react-i18next'
import { Checkbox, Input } from '@agion/ui'
import type { SettingsCardRange } from './infra_settings_rollout.service'
import { isValueDraftInvalid, valueText, type RangeDraft, type ValueDraft } from './settings_range_draft'

/**
 * The range editor of the settings card (PLAN-20261013 slice 08d): one
 * setting's default, its own range, and the fields that change it.
 */
interface ValueFieldProps {
  id: string
  label: string
  unit: string
  /** The server takes unlimited for this setting; otherwise no unlimited choice is offered. */
  allowsUnlimited: boolean
  draft: ValueDraft
  disabled: boolean
  onChange: (draft: ValueDraft) => void
}

function SettingValueField({ id, label, unit, allowsUnlimited, draft, disabled, onChange }: ValueFieldProps) {
  const { t } = useTranslation('pages')
  return (
    <div className="space-y-2">
      <Input
        id={id}
        label={`${label} (${unit})`}
        inputMode="numeric"
        value={draft.unlimited ? '' : draft.amount}
        disabled={disabled || draft.unlimited}
        onChange={(event) => onChange({ ...draft, amount: event.target.value })}
        error={isValueDraftInvalid(draft) ? t('rolloutControls.settings.wholeNumber') : undefined}
      />
      {allowsUnlimited && (
        <Checkbox
          id={`${id}-unlimited`}
          checked={draft.unlimited}
          disabled={disabled}
          onCheckedChange={(checked) => onChange({ ...draft, unlimited: checked === true })}
          label={t('rolloutControls.settings.unlimited')}
        />
      )}
    </div>
  )
}

interface RangeEditorProps {
  range: SettingsCardRange
  draft: RangeDraft
  disabled: boolean
  onChange: (draft: RangeDraft) => void
}

/** One setting: its default, its own range, and the fields that change it. */
export function SettingsRangeEditor({ range, draft, disabled, onChange }: RangeEditorProps) {
  const { t } = useTranslation('pages')
  const unlimitedText = t('rolloutControls.settings.unlimited')
  const id = `settings-range-${range.key.replace(/[^a-z0-9]+/gi, '-')}`
  return (
    <fieldset className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <legend className="px-1 text-sm font-medium text-slate-900 dark:text-slate-100">{range.name}</legend>
      <dl className="mb-3 grid gap-2 text-xs text-slate-500 sm:grid-cols-2 dark:text-slate-400">
        <div>
          <dt className="uppercase tracking-wide">{t('rolloutControls.settings.defaultRange')}</dt>
          <dd className="mt-1 font-mono text-slate-700 dark:text-slate-300">
            {t('rolloutControls.settings.rangeText', {
              minimum: valueText(range.default.minimum, range.unit, unlimitedText),
              maximum: valueText(range.default.maximum, range.unit, unlimitedText),
            })}
          </dd>
        </div>
        <div>
          <dt className="uppercase tracking-wide">{t('rolloutControls.settings.ownRange')}</dt>
          <dd className="mt-1 font-mono text-slate-700 dark:text-slate-300">
            {range.own
              ? t('rolloutControls.settings.rangeText', {
                minimum: valueText(range.own.minimum, range.unit, unlimitedText),
                maximum: valueText(range.own.maximum, range.unit, unlimitedText),
              })
              : t('rolloutControls.settings.usesDefault')}
          </dd>
        </div>
      </dl>
      <Checkbox
        id={`${id}-default`}
        checked={draft.useDefault}
        disabled={disabled}
        onCheckedChange={(checked) => onChange({ ...draft, useDefault: checked === true })}
        label={t('rolloutControls.settings.useDefault')}
      />
      {!draft.useDefault && (
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <SettingValueField
            id={`${id}-minimum`}
            label={t('rolloutControls.settings.minimum')}
            unit={range.unit}
            allowsUnlimited={range.allowsUnlimited}
            draft={draft.minimum}
            disabled={disabled}
            onChange={(minimum) => onChange({ ...draft, minimum })}
          />
          <SettingValueField
            id={`${id}-maximum`}
            label={t('rolloutControls.settings.maximum')}
            unit={range.unit}
            allowsUnlimited={range.allowsUnlimited}
            draft={draft.maximum}
            disabled={disabled}
            onChange={(maximum) => onChange({ ...draft, maximum })}
          />
        </div>
      )}
    </fieldset>
  )
}
